import { forEach } from 'lodash';
import createReReSelector from 're-reselect';

import { InterestsIdsType } from '../../components/InterestsSetEditor/InterestsSetEditor';
import { mapCurrentAudienceTargetings } from '../../mappers/audienceTargetings/audienceTargetings';
import { mapAudienceTargetingsToRetargetingConditionsApiFormat } from '../../mappers/retargetingCondition/retargetingCondition';
import {
    getPredictorInterestsText,
    getPredictorMetricsText,
    getPredictorPreviewText
} from '../../service/CryptaHelpers/CryptaHelpers';
import { AudienceTargetingsType } from '../../typings/audienceTargetings';
import { CryptaSegmentsByType } from '../../typings/cryptaSegments';
import {
    GdUpdateCpmRetargetingConditionItemInput,
    WebGoalType
} from '../../typings/graphql-grid';
import { EditCpmGroupDataValue } from '../../typings/groupsEditState';
import { TextPredictor } from '../../typings/predictor';
import { RetargetingConditionData } from '../../typings/retargeting';
import { State } from '../../typings/state';
import {
    getAllCryptaSegmentsByType,
    getAllSegmentsGroupByType,
    getInterestsIds
} from '../cryptaSegments/cryptaSegments';
import { selectEditGroupValueInfo } from '../groupEdit/group/group';

export const getCurrentAudienceTargetingsServerValue = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State, groupId: string) => ({
        interestsTargetingEditor: state.interestsTargetingEditor[groupId],
        cryptaSocialDemoEditor: state.cryptaSocialDemoEditor[groupId],
        cryptaGroupFamilyEditor: state.cryptaGroupFamilyEditor[groupId],
        cryptaAudioGenresEditor: state.cryptaAudioGenresEditor[groupId],
        cryptaBehavioralEditor: state.cryptaBehavioralEditor[groupId]
    }),

    (state: State) => getAllCryptaSegmentsByType(state),
    (
        groupValue: EditCpmGroupDataValue,
        audienceTargetingsEditors,
        cryptaSegmentsByType: CryptaSegmentsByType
    ): GdUpdateCpmRetargetingConditionItemInput => {
        return mapAudienceTargetingsToRetargetingConditionsApiFormat({
            audienceTargetings: mapCurrentAudienceTargetings(
                audienceTargetingsEditors,
                groupValue.audienceTargetings
            ),
            audienceTargetingsTemplateName:
                groupValue.audienceTargetingsTemplateName,
            cryptaSegmentsByType: cryptaSegmentsByType
        });
    }
)((state, groupId) => groupId);

export const getCurrentAudienceTargetingsText = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State, groupId: string) => ({
        interestsTargetingEditor: state.interestsTargetingEditor[groupId],
        cryptaSocialDemoEditor: state.cryptaSocialDemoEditor[groupId],
        cryptaGroupFamilyEditor: state.cryptaGroupFamilyEditor[groupId],
        cryptaAudioGenresEditor: state.cryptaAudioGenresEditor[groupId],
        cryptaBehavioralEditor: state.cryptaBehavioralEditor[groupId]
    }),
    (state: State) => getInterestsIds(state),
    (state: State) => getAllSegmentsGroupByType(state),
    (
        groupValue: EditCpmGroupDataValue,
        audienceTargetingsEditors,
        interestId: InterestsIdsType,
        cryptaSegmentsByType: CryptaSegmentsByType
    ): TextPredictor[] => {
        const audienceTargetings = mapCurrentAudienceTargetings(
            audienceTargetingsEditors,
            groupValue.audienceTargetings
        );

        let result: TextPredictor[] = [];

        forEach(audienceTargetings, (value, key) => {
            if (key === AudienceTargetingsType.interests) {
                result = result.concat(
                    getPredictorInterestsText(
                        interestId,
                        audienceTargetings[key]
                    )
                );
            } else if (key === AudienceTargetingsType.socialDemo) {
                result = result.concat(
                    getPredictorPreviewText(
                        cryptaSegmentsByType[WebGoalType.social_demo],
                        audienceTargetings[key]
                    )
                );
            } else if (key === AudienceTargetingsType.audioGenres) {
                result = result.concat(
                    getPredictorPreviewText(
                        cryptaSegmentsByType[WebGoalType.audio_genres],
                        audienceTargetings[key]
                    )
                );
            } else {
                result = result.concat(
                    getPredictorPreviewText(
                        cryptaSegmentsByType[key],
                        audienceTargetings[key]
                    )
                );
            }
        });

        return result;
    }
)((state, groupId) => groupId);

export const getAudienceMetricsText = createReReSelector(
    (state: State, groupId: string) => getAudienceMetricsValue(state, groupId),
    (audienceMetrics?: RetargetingConditionData): TextPredictor[] => {
        return getPredictorMetricsText(audienceMetrics);
    }
)((state, groupId) => groupId);

export const getAudienceMetricsValue = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (
        groupValue: EditCpmGroupDataValue
    ): RetargetingConditionData | undefined => {
        return groupValue.audienceTargetings[
            AudienceTargetingsType.audienceMetrics
        ];
    }
)((state, groupId) => groupId);
